/**
 * API Service Client
 * 
 * Handles all API communication with the inference backend.
 * Configure via environment variables (see src/config.js):
 * - VITE_API_URL: Base URL for the API
 */

import { API_BASE_URL } from '../config'

// In-memory cache for cities and coordinates (24h TTL; server uses same 24h and invalidates on POST /api/v1/cache/clear after data updates)
const FRONTEND_CACHE_TTL_MS = 24 * 60 * 60 * 1000
const _frontendCache = { cities: null, citiesExpiry: 0, coordinates: null, coordinatesExpiry: 0 }

/**
 * Get default headers for API requests
 * @returns {Object} Headers object with content type
 */
const getHeaders = () => ({
  'Content-Type': 'application/json'
})

/**
 * Get headers with optional Authorization Bearer (access token from auth service).
 * Used for all tier-aware endpoints: predictions, comps, predict, comps/custom.
 * Backend uses the token to determine user tier and filter premium fields accordingly.
 * @returns {Promise<Object>} Headers with optionally Authorization
 */
const getHeadersWithAuth = async () => {
  const headers = { ...getHeaders() }
  try {
    const { getAccessToken } = await import('./auth')
    const token = getAccessToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  } catch (_) {
    // ignore
  }
  return headers
}

/**
 * Handle API response errors
 * @param {Response} response - Fetch response object
 * @returns {Promise<Response>} Response if successful, throws error otherwise
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`
    let errorData = null
    
    try {
      errorData = await response.json()
      errorMessage = errorData.detail || errorData.message || errorMessage
    } catch (e) {
      // If response is not JSON, try to get text
      try {
        const errorText = await response.text()
        if (errorText) {
          errorMessage = errorText
        }
      } catch (textError) {
        // Fall back to status-based message
      }
    }
    
    const error = new Error(errorMessage)
    error.status = response.status
    error.statusText = response.statusText
    error.data = errorData
    error.isApiError = true
    
    // Check for Retry-After header for rate limiting
    const retryAfter = response.headers.get('Retry-After')
    if (retryAfter) {
      error.retryAfter = parseInt(retryAfter, 10)
    }
    
    throw error
  }
  
  return response
}

/**
 * Property prediction request features
 * @typedef {Object} PropertyFeatures
 * @property {string} neighbourhood - Neighborhood name
 * @property {number} bathroomsTotal - Total bathrooms
 * @property {number} bedroomsTotal - Total bedrooms
 * @property {number} livingArea - Living area in square feet
 * @property {number} [halfBaths] - Half bathrooms (optional)
 * @property {number} [lot_size] - Lot size in square feet (optional)
 * @property {number} [propertyAge] - Property age in years (optional)
 * @property {number} [overall_condition] - Overall condition rating (optional)
 * @property {number} [hasGarage] - Has garage (0 or 1) (optional)
 * @property {number} [isWaterfront] - Is waterfront (0 or 1) (optional)
 * @property {string} [houseStyle] - House style (for houses only) (optional)
 * @property {number} [lat] - Latitude (optional)
 * @property {number} [lon] - Longitude (optional)
 */

/**
 * Prediction request payload
 * @typedef {Object} PredictionRequest
 * @property {string} city - City name (e.g., "Toronto")
 * @property {string} [property_type] - Property type: "condo" or "house" (optional, auto-detected if not provided)
 * @property {PropertyFeatures} features - Property features
 */

/**
 * Prediction response
 * @typedef {Object} PredictionResponse
 * @property {number} predicted_price - Predicted property price
 * @property {number} confidence_lower - Lower bound of confidence interval
 * @property {number} confidence_upper - Upper bound of confidence interval
 * @property {string} city - City name
 * @property {string} property_type - Property type ("condo" or "house")
 * @property {string} model_version - Model version used for prediction
 */

/**
 * Get property price prediction
 * 
 * @param {PredictionRequest} request - Prediction request with city and features
 * @returns {Promise<PredictionResponse>} Prediction response with price and confidence intervals
 * @throws {Error} If the API request fails
 * 
 * @example
 * const prediction = await predict({
 *   city: "Toronto",
 *   property_type: "house",
 *   features: {
 *     neighbourhood: "Downtown",
 *     bathroomsTotal: 2.5,
 *     bedroomsTotal: 3,
 *     livingArea: 1500,
 *     lot_size: 5000,
 *     propertyAge: 25,
 *     overall_condition: 4.0,
 *     hasGarage: 1,
 *     isWaterfront: 0,
 *     houseStyle: "Detached"
 *   }
 * })
 */
export const predict = async (request) => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 90000) // 90 second timeout (ML inference can be slow)
    const headers = await getHeadersWithAuth()

    const response = await fetch(`${API_BASE_URL}/api/v1/predict`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal: controller.signal
    })
    
    clearTimeout(timeoutId)
    const validatedResponse = await handleResponse(response)
    const data = await validatedResponse.json()
    
    return data
  } catch (error) {
    // Handle abort (timeout)
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timeout. Please try again.')
      timeoutError.name = 'TimeoutError'
      timeoutError.isTimeout = true
      throw timeoutError
    }
    
    // Enhance error with context
    if (error.status) {
      // HTTP error (already handled by handleResponse)
      throw error
    } else {
      // Network or other error
      const networkError = new Error(
        error.message || 'Network error. Please check your connection and try again.'
      )
      networkError.originalError = error
      networkError.isNetworkError = true
      throw networkError
    }
  }
}

/**
 * Run one guest prediction tied to a paid quick_predict Stripe session.
 *
 * @param {Object} request
 * @param {string} request.stripeSessionId
 * @param {string} request.city
 * @param {string} [request.property_type]
 * @param {Object} request.features
 * @param {string} [request.address]
 * @returns {Promise<PredictionResponse>}
 */
export const predictGuest = async (request) => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 90000)
    const response = await fetch(`${API_BASE_URL}/api/v1/predict/guest`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(request),
      signal: controller.signal,
    })
    clearTimeout(timeoutId)
    const validatedResponse = await handleResponse(response)
    return await validatedResponse.json()
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timeout. Please try again.')
      timeoutError.name = 'TimeoutError'
      timeoutError.isTimeout = true
      throw timeoutError
    }
    throw error
  }
}

/**
 * Free preview prediction — no auth required, IP rate limited to 1/day.
 * Returns full prediction result; frontend gates premium details behind paywall.
 */
export const predictPreview = async (request) => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 90000)
    const response = await fetch(`${API_BASE_URL}/api/v1/predict/preview`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(request),
      signal: controller.signal,
    })
    clearTimeout(timeoutId)
    if (response.status === 429) {
      const err = new Error('Daily preview limit reached. Purchase a report for unlimited predictions.')
      err.status = 429
      err.isRateLimited = true
      throw err
    }
    const validatedResponse = await handleResponse(response)
    return await validatedResponse.json()
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timeout. Please try again.')
      timeoutError.name = 'TimeoutError'
      timeoutError.isTimeout = true
      throw timeoutError
    }
    throw error
  }
}

/**
 * Lightweight prediction for comp accuracy checks — does NOT count against usage quota.
 * Returns only predicted_price, confidence bounds, and property_type.
 * 
 * @param {Object} request - Same shape as predict() request
 * @returns {Promise<Object>} { predicted_price, confidence_lower, confidence_upper, property_type }
 */
export const predictCompCheck = async (request) => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30s timeout (lighter than full predict)
    const headers = await getHeadersWithAuth()

    const response = await fetch(`${API_BASE_URL}/api/v1/predict/comp-check`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal: controller.signal
    })

    clearTimeout(timeoutId)
    const validatedResponse = await handleResponse(response)
    return await validatedResponse.json()
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Comp check timeout.')
      timeoutError.name = 'TimeoutError'
      throw timeoutError
    }
    throw error
  }
}

/**
 * Check if an address is eligible for one-time prediction purchase.
 *
 * @param {Object} request
 * @param {string} [request.city]
 * @param {string} [request.property_type]
 * @param {string} [request.address]
 * @param {number} [request.lat]
 * @param {number} [request.lon]
 * @returns {Promise<{eligible:boolean, reason:string, message:string, resolved_city?:string, resolved_neighbourhood?:string}>}
 */
export const checkPredictionEligibility = async (request) => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000)
    const headers = await getHeadersWithAuth()
    const response = await fetch(`${API_BASE_URL}/api/v1/predict/eligibility-check`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request || {}),
      signal: controller.signal,
    })
    clearTimeout(timeoutId)
    const validated = await handleResponse(response)
    return await validated.json()
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Eligibility check timeout. Please try again.')
      timeoutError.name = 'TimeoutError'
      throw timeoutError
    }
    throw error
  }
}

/**
 * Get list of available cities
 * 
 * @returns {Promise<Array<Object>>} Array of cities with available models
 * @throws {Error} If the API request fails
 * 
 * @example
 * const cities = await getCities()
 * // Returns: [{ city: "Toronto", lat: 43.6532, lon: -79.3832, ... }, ...]
 */
export const getCities = async () => {
  const now = Date.now()
  if (_frontendCache.cities != null && now < _frontendCache.citiesExpiry) {
    return _frontendCache.cities
  }
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000) // 15 second timeout
    
    const response = await fetch(`${API_BASE_URL}/api/v1/cities`, {
      method: 'GET',
      headers: getHeaders(),
      signal: controller.signal
    })
    
    clearTimeout(timeoutId)
    const validatedResponse = await handleResponse(response)
    const data = await validatedResponse.json()
    _frontendCache.cities = data
    _frontendCache.citiesExpiry = now + FRONTEND_CACHE_TTL_MS
    return data
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timeout. Please try again.')
      timeoutError.name = 'TimeoutError'
      throw timeoutError
    }
    
    if (error.status) {
      throw error
    } else {
      const networkError = new Error(
        error.message || 'Failed to fetch cities. Please check your connection and try again.'
      )
      networkError.originalError = error
      networkError.isNetworkError = true
      throw networkError
    }
  }
}

/**
 * Get coordinates for ALL cities (including metro area sub-cities)
 * 
 * Used by the map to position city markers at actual city centers.
 * 
 * @returns {Promise<Array>} Array of objects with city, lat, lon
 * 
 * @example
 * const coords = await getCityCoordinates()
 * // Returns: [{ city: "Burnaby", lat: 49.2488, lon: -122.9805 }, ...]
 */
export const getCityCoordinates = async () => {
  const now = Date.now()
  if (_frontendCache.coordinates != null && now < _frontendCache.coordinatesExpiry) {
    return _frontendCache.coordinates
  }
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout
    
    const response = await fetch(`${API_BASE_URL}/api/v1/city-coordinates`, {
      method: 'GET',
      headers: getHeaders(),
      signal: controller.signal
    })
    
    clearTimeout(timeoutId)
    const validatedResponse = await handleResponse(response)
    const data = await validatedResponse.json()
    _frontendCache.coordinates = data
    _frontendCache.coordinatesExpiry = now + FRONTEND_CACHE_TTL_MS
    return data
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timed out while fetching city coordinates.')
      timeoutError.isTimeout = true
      throw timeoutError
    }
    
    if (error.status) {
      throw error
    } else {
      const networkError = new Error(
        error.message || 'Failed to fetch city coordinates.'
      )
      networkError.originalError = error
      networkError.isNetworkError = true
      throw networkError
    }
  }
}

/**
 * Get available property types for a specific city
 * 
 * @param {string} city - City name (e.g., "Toronto")
 * @returns {Promise<Object>} Object with city and property_types array
 * @throws {Error} If the API request fails
 * 
 * @example
 * const result = await getPropertyTypes("Toronto")
 * // Returns: { city: "Toronto", property_types: ["condo", "house"] }
 */
export const getPropertyTypes = async (city) => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000)
    
    const response = await fetch(
      `${API_BASE_URL}/api/v1/cities/${encodeURIComponent(city)}/property-types`,
      {
        method: 'GET',
        headers: getHeaders(),
        signal: controller.signal
      }
    )
    
    clearTimeout(timeoutId)
    const validatedResponse = await handleResponse(response)
    const data = await validatedResponse.json()
    
    return data
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timeout. Please try again.')
      timeoutError.name = 'TimeoutError'
      throw timeoutError
    }
    
    if (error.status) {
      throw error
    } else {
      const networkError = new Error(
        error.message || `Failed to fetch property types for ${city}. Please check your connection and try again.`
      )
      networkError.originalError = error
      networkError.isNetworkError = true
      throw networkError
    }
  }
}

/**
 * Get optional feature support for a city (e.g. whether basement_apartment is modelled).
 * Returns safe defaults (all true) on error so the UI never incorrectly disables inputs.
 */
export const getCityFeatures = async (city) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/cities/${encodeURIComponent(city)}/features`,
      { method: 'GET', headers: getHeaders() }
    )
    if (!response.ok) return { basement_apartment: true, is_waterfront: true }
    return await response.json()
  } catch {
    return { basement_apartment: true, is_waterfront: true }
  }
}

/**
 * Address autocomplete search
 * 
 * @param {string} query - Search query (address or postal code)
 * @param {number} [limit=5] - Maximum number of results (default: 5)
 * @returns {Promise<Object>} Autocomplete results from Stadia Maps
 * @throws {Error} If the API request fails
 * 
 * @example
 * const results = await autocomplete("123 Main St, Toronto", 5)
 */
export const autocomplete = async (query, limit = 5) => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout for autocomplete
    
    const response = await fetch(
      `${API_BASE_URL}/api/v1/geocoding/autocomplete?q=${encodeURIComponent(query)}&limit=${limit}`,
      {
        method: 'GET',
        headers: getHeaders(),
        signal: controller.signal
      }
    )
    
    clearTimeout(timeoutId)
    const validatedResponse = await handleResponse(response)
    const data = await validatedResponse.json()
    
    return data
  } catch (error) {
    // Don't throw on abort for autocomplete - just return empty results
    if (error.name === 'AbortError') {
      return { features: [] }
    }
    
    if (error.status) {
      throw error
    } else {
      const networkError = new Error(
        error.message || 'Failed to search address. Please check your connection and try again.'
      )
      networkError.originalError = error
      networkError.isNetworkError = true
      throw networkError
    }
  }
}

/**
 * Geocode address to coordinates
 * 
 * @param {string} address - Address string
 * @param {string} [city] - Optional city name for better accuracy
 * @returns {Promise<Object>} Geocoded address with lat/lon
 * @throws {Error} If the API request fails
 * 
 * @example
 * const result = await geocodeAddress("123 Main St, Toronto", "Toronto")
 * // Returns: { address: "123 Main St, Toronto", lat: 43.6532, lon: -79.3832 }
 */
export const geocodeAddress = async (address, city = null) => {
  try {
    let url = `${API_BASE_URL}/api/v1/geocoding/address?address=${encodeURIComponent(address)}`
    if (city) {
      url += `&city=${encodeURIComponent(city)}`
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders()
    })
    
    const validatedResponse = await handleResponse(response)
    const data = await validatedResponse.json()
    
    return data
  } catch (error) {
    if (error.status) {
      throw error
    } else {
      const networkError = new Error(
        `Failed to geocode address: ${error.message}`
      )
      networkError.originalError = error
      throw networkError
    }
  }
}

/**
 * Get neighborhood from coordinates
 * 
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @param {string} city - City name
 * @returns {Promise<Object>} Neighborhood information
 * @throws {Error} If the API request fails
 * 
 * @example
 * const result = await getNeighborhood(43.6532, -79.3832, "Toronto")
 * // Returns: { neighbourhood: "Downtown" }
 */
export const getNeighborhood = async (lat, lon, city) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/geocoding/neighbourhood?lat=${lat}&lon=${lon}&city=${encodeURIComponent(city)}`,
      {
        method: 'GET',
        headers: getHeaders()
      }
    )
    
    const validatedResponse = await handleResponse(response)
    const data = await validatedResponse.json()
    
    return data
  } catch (error) {
    if (error.status) {
      throw error
    } else {
      const networkError = new Error(
        `Failed to get neighborhood: ${error.message}`
      )
      networkError.originalError = error
      throw networkError
    }
  }
}

/**
 * Get city predictions (premade predictions for a city)
 * 
 * @param {string} city - City name (e.g., "Toronto", "Vancouver")
 * @param {number} [limit] - Optional limit on number of predictions per property type
 * @returns {Promise<Object>} Object with city, house_predictions, and condo_predictions arrays
 * @throws {Error} If the API request fails
 * 
 * @example
 * const result = await getCityPredictions("Toronto", 50)
 * // Returns: { city: "Toronto", house_predictions: [...], condo_predictions: [...] }
 */
export const getCityPredictions = async (city, limit = null, filterCity = null, highConfidenceOnly = false, orderBy = 'confidence', propertyType = null, neighbourhood = null) => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout
    
    let url = `${API_BASE_URL}/api/v1/predictions/${encodeURIComponent(city)}`
    const params = []
    if (limit) params.push(`limit=${limit}`)
    if (filterCity) params.push(`filter_city=${encodeURIComponent(filterCity)}`)
    if (highConfidenceOnly) params.push(`high_confidence_only=true`)
    if (orderBy) params.push(`order_by=${encodeURIComponent(orderBy)}`)
    if (propertyType) params.push(`property_type=${encodeURIComponent(propertyType)}`)
    if (neighbourhood) params.push(`neighbourhood=${encodeURIComponent(neighbourhood)}`)
    if (params.length > 0) url += `?${params.join('&')}`
    
    const headers = await getHeadersWithAuth()
    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal: controller.signal
    })
    
    clearTimeout(timeoutId)
    const validatedResponse = await handleResponse(response)
    const data = await validatedResponse.json()
    
    return data
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timeout. Please try again.')
      timeoutError.name = 'TimeoutError'
      timeoutError.isTimeout = true
      throw timeoutError
    }
    
    if (error.status) {
      throw error
    } else {
      const networkError = new Error(
        error.message || `Failed to fetch predictions for ${city}. Please check your connection and try again.`
      )
      networkError.originalError = error
      networkError.isNetworkError = true
      throw networkError
    }
  }
}

/**
 * Search precomputed predictions by address text.
 *
 * @param {string} query - Address-like search text (e.g. "123 Main St")
 * @param {string|null} city - Optional city scope
 * @param {number} [limit=5] - Max matches to return
 * @returns {Promise<{query:string, city_scope:string|null, count:number, matches:Array}>}
 */
export const searchPredictionsByAddress = async (query, city = null, limit = 5) => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 12000) // 12 second timeout

    const params = new URLSearchParams()
    params.set('q', query)
    if (city) params.set('city', city)
    if (limit) params.set('limit', String(limit))

    const headers = await getHeadersWithAuth()
    const response = await fetch(`${API_BASE_URL}/api/v1/predictions/search/address?${params.toString()}`, {
      method: 'GET',
      headers,
      signal: controller.signal
    })

    clearTimeout(timeoutId)
    const validatedResponse = await handleResponse(response)
    return await validatedResponse.json()
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Address search timeout. Please try again.')
      timeoutError.name = 'TimeoutError'
      timeoutError.isTimeout = true
      throw timeoutError
    }

    if (error.status) throw error
    const networkError = new Error(error.message || 'Failed to search predictions by address.')
    networkError.originalError = error
    networkError.isNetworkError = true
    throw networkError
  }
}

/**
 * Resolve a Realtor.ca URL (canonical or shortlink) to a canonical URL containing the numeric id.
 *
 * - Canonical URLs are returned immediately by the backend without a network hop.
 * - Shortlinks (/l/…) are resolved server-side via an HTTP HEAD redirect follow.
 *
 * @param {string} url - The raw URL pasted by the user
 * @returns {Promise<{ resolved_url: string }>}
 */
export const resolveListingUrl = async (url) => {
  if (!url) throw new Error('url is required')
  const headers = await getHeadersWithAuth()
  const response = await fetch(`${API_BASE_URL}/api/v1/resolve-listing-url`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const error = new Error(errorData.detail || 'Could not resolve listing URL')
    error.status = response.status
    throw error
  }
  return response.json()
}

/**
 * Resolve a Realtor.ca numeric listing id to an internal prediction record.
 *
 * Realtor.ca URLs embed a numeric id: /real-estate/{listingId}/{slug}
 * Returns { property_id, address, city, property_type, neighbourhood,
 *           bedrooms_total, bathrooms_total, living_area, predicted_price }
 * Throws a 404-shaped error when the listing is not in current predictions.
 *
 * @param {string|number} listingId - Numeric id parsed from the pasted URL
 * @returns {Promise<Object>}
 */
export const getPredictionByListingId = async (listingId) => {
  if (!listingId) throw new Error('listingId is required')
  const headers = await getHeadersWithAuth()
  const response = await fetch(
    `${API_BASE_URL}/api/v1/predictions/by-listing-id/${encodeURIComponent(listingId)}`,
    { method: 'GET', headers }
  )
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const error = new Error(errorData.detail || `Listing ${listingId} not found in current predictions`)
    error.status = response.status
    throw error
  }
  return response.json()
}

/**
 * Get landing page preview predictions (single request: 6 items, 4 high / 1 med / 1 low, city variety).
 * Uses optional auth for tier-based field stripping.
 * @returns {Promise<{ predictions: Array }>}
 */
export const getLandingPreviewPredictions = async () => {
  const headers = await getHeadersWithAuth()
  const response = await fetch(`${API_BASE_URL}/api/v1/predictions/landing-preview`, {
    method: 'GET',
    headers
  })
  const validatedResponse = await handleResponse(response)
  const data = await validatedResponse.json()
  return data
}

/**
 * Get predictions by viewport (bounds)
 * 
 * @param {string} city - City name (e.g., "Toronto")
 * @param {Object} bounds - Bounding box with sw (southwest) and ne (northeast) coordinates
 * @param {number} [limit=5000] - Maximum number of predictions to return
 * @returns {Promise<Object>} Object with city, house_predictions, and condo_predictions arrays
 * @throws {Error} If the API request fails
 * 
 * @example
 * const result = await getPredictionsByViewport("Toronto", {
 *   sw: { lat: 43.6, lng: -79.4 },
 *   ne: { lat: 43.7, lng: -79.3 }
 * }, 1000)
 */
export const getPredictionsByViewport = async (city, bounds, limit = 5000) => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout
    
    const sw = bounds.getSouthWest ? bounds.getSouthWest() : bounds.sw
    const ne = bounds.getNorthEast ? bounds.getNorthEast() : bounds.ne
    
    const url = `${API_BASE_URL}/api/v1/predictions/${encodeURIComponent(city)}/viewport?sw_lat=${sw.lat}&sw_lon=${sw.lng}&ne_lat=${ne.lat}&ne_lon=${ne.lng}&limit=${limit}`
    
    const headers = await getHeadersWithAuth()
    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal: controller.signal
    })
    
    clearTimeout(timeoutId)
    const validatedResponse = await handleResponse(response)
    const data = await validatedResponse.json()
    
    return data
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timeout. Please try again.')
      timeoutError.name = 'TimeoutError'
      timeoutError.isTimeout = true
      throw timeoutError
    }
    
    if (error.status) {
      throw error
    } else {
      const networkError = new Error(
        error.message || `Failed to fetch viewport predictions for ${city}. Please check your connection and try again.`
      )
      networkError.originalError = error
      networkError.isNetworkError = true
      throw networkError
    }
  }
}

/**
 * Get neighborhood counts for a city
 * 
 * @param {string} city - City name (e.g., "Toronto", "Vancouver")
 * @returns {Promise<Object>} Object with neighborhood counts including lat/lon for each neighborhood
 * @throws {Error} If the API request fails
 * 
 * @example
 * const result = await getNeighborhoodCounts("Toronto")
 * // Returns: { "Downtown": { lat: 43.65, lon: -79.38, house_count: 50, condo_count: 100, total_count: 150 }, ... }
 */
export const getNeighborhoodCounts = async (city) => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000) // 15 second timeout
    
    const response = await fetch(
      `${API_BASE_URL}/api/v1/predictions/${encodeURIComponent(city)}/neighbourhoods/counts`,
      {
        method: 'GET',
        headers: getHeaders(),
        signal: controller.signal
      }
    )
    
    clearTimeout(timeoutId)
    const validatedResponse = await handleResponse(response)
    const data = await validatedResponse.json()
    
    return data
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timeout. Please try again.')
      timeoutError.name = 'TimeoutError'
      throw timeoutError
    }
    
    if (error.status) {
      throw error
    } else {
      const networkError = new Error(
        error.message || `Failed to fetch neighborhood counts for ${city}. Please check your connection and try again.`
      )
      networkError.originalError = error
      networkError.isNetworkError = true
      throw networkError
    }
  }
}

/**
 * Get prediction counts for all cities.
 *
 * Lightweight endpoint used for UI metadata (city tabs + total counts) so
 * browse can show full metro totals without loading every prediction row.
 *
 * @returns {Promise<Object>} { [cityName]: { house_count, condo_count, total_count } }
 */
export const getAllCityPredictionCounts = async () => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000)

    const response = await fetch(`${API_BASE_URL}/api/v1/predictions/counts/all`, {
      method: 'GET',
      headers: getHeaders(),
      signal: controller.signal,
    })

    clearTimeout(timeoutId)
    const validatedResponse = await handleResponse(response)
    const data = await validatedResponse.json()
    return data || {}
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timeout. Please try again.')
      timeoutError.name = 'TimeoutError'
      timeoutError.isTimeout = true
      throw timeoutError
    }
    if (error.status) throw error
    const networkError = new Error(
      error.message || 'Failed to fetch city prediction counts. Please check your connection and try again.'
    )
    networkError.originalError = error
    networkError.isNetworkError = true
    throw networkError
  }
}

/**
 * Get accuracy metrics (error rates) for a city and its neighborhoods
 * Similar to Zillow/Redfin accuracy reporting
 * 
 * @param {string} city - City name (e.g., "Toronto", "Vancouver")
 * @param {string} propertyType - Property type: 'house' or 'condo' (default: 'house')
 * @param {string} [neighborhood] - Optional: Specific neighborhood to highlight
 * @returns {Promise<Object>} Object with city_metrics, property_neighborhood, top_3_most_accurate, bottom_3_least_accurate, and all_neighborhoods
 * @throws {Error} If the API request fails
 * 
 * @example
 * const result = await getCityAccuracy("Toronto", "house", "Downtown")
 * // Returns: { city_metrics: {...}, property_neighborhood: {...}, top_3_most_accurate: [...], ... }
 */
export const getCityAccuracy = async (city, propertyType = 'house', neighborhood = null, _retryCount = 0) => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout
    
    let url = `${API_BASE_URL}/api/v1/cities/${encodeURIComponent(city)}/accuracy?property_type=${encodeURIComponent(propertyType)}`
    if (neighborhood) {
      url += `&neighborhood=${encodeURIComponent(neighborhood)}`
    }
    
    const response = await fetch(
      url,
      {
        method: 'GET',
        headers: getHeaders(),
        signal: controller.signal
      }
    )
    
    clearTimeout(timeoutId)
    const validatedResponse = await handleResponse(response)
    const data = await validatedResponse.json()
    
    return data
  } catch (error) {
    if (error.name === 'AbortError') {
      // Retry once after timeout (server may have been busy with the prediction)
      if (_retryCount < 1) {
        console.warn(`Accuracy metrics timed out for ${city}, retrying...`)
        await new Promise(r => setTimeout(r, 2000)) // Wait 2s before retry
        return getCityAccuracy(city, propertyType, neighborhood, _retryCount + 1)
      }
      const timeoutError = new Error('Request timeout. Please try again.')
      timeoutError.name = 'TimeoutError'
      throw timeoutError
    }
    
    if (error.status) {
      throw error
    } else {
      const networkError = new Error(
        error.message || `Failed to fetch accuracy metrics for ${city}. Please check your connection and try again.`
      )
      networkError.originalError = error
      networkError.isNetworkError = true
      throw networkError
    }
  }
}

/**
 * Get market insights (overpricing/underpricing trends) for a city and neighborhood
 * @param {string} city - City name
 * @param {string} propertyType - Property type ('house' or 'condo')
 * @param {string|null} neighborhood - Optional neighborhood name
 * @returns {Promise<Object>} Market insights data
 */
export const getCityMarketInsights = async (city, propertyType = 'house', neighborhood = null) => {
  if (!city) {
    throw new Error('City is required')
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout

  let url = `${API_BASE_URL}/api/v1/cities/${encodeURIComponent(city)}/market-insights?property_type=${encodeURIComponent(propertyType)}`
  if (neighborhood) {
    url += `&neighbourhood=${encodeURIComponent(neighborhood)}`
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal
    })
    clearTimeout(timeoutId)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const error = new Error(errorData.detail || `Failed to fetch market insights for ${city}`)
      error.status = response.status
      throw error
    }

    return await response.json()
  } catch (error) {
    if (error.status) {
      throw error
    } else {
      const networkError = new Error(
        error.message || `Failed to fetch market insights for ${city}. Please check your connection and try again.`
      )
      networkError.originalError = error
      networkError.isNetworkError = true
      throw networkError
    }
  }
}

/**
 * Get precomputed city stats summary (accuracy + market insights) for all cities.
 * One request loads all city cards; regenerate via admin POST /api/v1/admin/regenerate-city-stats.
 * @param {string} propertyType - Property type ('house' or 'condo'), default 'house'
 * @returns {Promise<{ generated_at: string|null, property_type: string, cities: Object }>}
 */
export const getCityStatsSummary = async (propertyType = 'house') => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/cities/stats-summary?property_type=${encodeURIComponent(propertyType)}`,
      { method: 'GET', headers: getHeaders() }
    )
    if (!response.ok) {
      if (response.status === 404) {
        return { generated_at: null, property_type: propertyType, cities: {} }
      }
      const err = await response.json().catch(() => ({}))
      const e = new Error(err.detail || 'Failed to load city stats summary')
      e.status = response.status
      throw e
    }
    return await response.json()
  } catch (error) {
    if (error.status === 404) {
      return { generated_at: null, property_type: propertyType, cities: {} }
    }
    throw error
  }
}

/**
 * Get YoY price trends for all neighbourhoods in a city.
 * @param {string} city
 * @param {string} propertyType - 'house' or 'condo'
 * @returns {Promise<{ city_trend: Object|null, neighbourhood_trends: Array }>}
 */
export const getNeighbourhoodTrends = async (city, propertyType = 'house') => {
  if (!city) throw new Error('City is required')
  const response = await fetch(
    `${API_BASE_URL}/api/v1/cities/${encodeURIComponent(city)}/neighbourhood-trends?property_type=${encodeURIComponent(propertyType)}`,
    { method: 'GET', headers: getHeaders() }
  )
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    const e = new Error(err.detail || 'Failed to load neighbourhood trends')
    e.status = response.status
    throw e
  }
  return await response.json()
}

export const getCitySoldStats = async (city, propertyType = 'house') => {
  if (!city) throw new Error('City is required')
  const response = await fetch(
    `${API_BASE_URL}/api/v1/cities/${encodeURIComponent(city)}/sold-stats?property_type=${encodeURIComponent(propertyType)}`,
    { method: 'GET', headers: getHeaders() }
  )
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    const e = new Error(err.detail || 'Failed to load sold stats')
    e.status = response.status
    throw e
  }
  return await response.json()
}

// ============================================================================
// COMPARABLE PROPERTIES (COMPS)
// ============================================================================

/**
 * Get comparable properties for a specific property ID (premium feature)
 * 
 * @param {string} propertyId - The property ID
 * @param {string} propertyType - Property type: 'house' or 'condo'
 * @param {number} [limit=10] - Maximum number of comps to return
 * @param {string} [city] - City name (improves local file lookup on the server)
 * @param {string|null} [unlockSessionId] - Guest quick-unlock Stripe session to request full comps
 * @returns {Promise<{comps:Array, count:number, expandedAreaSearch:boolean, property_id?:string, property_type?:string, city?:string}>}
 * Full comps response object.
 */
export const getCompsByPropertyId = async (propertyId, propertyType, limit = 10, city = null, unlockSessionId = null) => {
  if (!propertyId) {
    throw new Error('Property ID is required')
  }
  if (!propertyType || !['house', 'condo'].includes(propertyType)) {
    throw new Error('Property type must be "house" or "condo"')
  }

  let url = `${API_BASE_URL}/api/v1/comps/${encodeURIComponent(propertyId)}?property_type=${encodeURIComponent(propertyType)}&limit=${limit}`
  if (city) {
    url += `&city=${encodeURIComponent(city)}`
  }
  if (unlockSessionId) {
    url += `&unlock_session_id=${encodeURIComponent(unlockSessionId)}`
  }

  try {
    const headers = await getHeadersWithAuth()
    const response = await fetch(url, {
      method: 'GET',
      headers
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const error = new Error(errorData.detail || `Failed to fetch comps for property ${propertyId}`)
      error.status = response.status
      throw error
    }

    const data = await response.json()

    return {
      property_id: data.property_id,
      property_type: data.property_type,
      city: data.city,
      comps: data.comps || [],
      count: data.count ?? (data.comps || []).length,
      expandedAreaSearch: Boolean(data.expanded_area_search),
    }
  } catch (error) {
    if (error.status) {
      throw error
    } else {
      const networkError = new Error(
        error.message || `Failed to fetch comparable properties. Please check your connection and try again.`
      )
      networkError.originalError = error
      networkError.isNetworkError = true
      throw networkError
    }
  }
}

/**
 * Get comparable properties for a custom property (on-the-fly; no property_id in DB).
 * Used by the custom prediction page. Returns comps with simple adjusted values.
 *
 * @param {Object} params
 * @param {string} params.city - City name
 * @param {string} params.propertyType - 'house' or 'condo'
 * @param {number} params.lat - Latitude
 * @param {number} params.lon - Longitude
 * @param {number} params.sqft - Living area sqft
 * @param {number} params.bedrooms - Bedrooms
 * @param {number} params.bathrooms - Bathrooms
 * @param {number} [params.yearBuilt] - Year built (optional)
 * @param {number} [params.lotSize] - Lot size sqft (optional, houses)
 * @param {string} [params.houseStyle] - House style (optional, houses)
 * @param {string} [params.neighbourhood] - Neighbourhood name (optional)
 * @param {number} [params.limit=10] - Max comps to return
 * @param {number} [params.predictedPrice] - Subject predicted price (for better adjusted comp values)
 * @param {number} [params.hasGarage] - 1 if subject has garage, 0 otherwise (houses only)
 * @param {number} [params.overallCondition] - Subject overall condition (1-5 scale)
 * @param {string} [params.stripeSessionId] - Guest quick_predict paid session for unauthenticated comps access
 * @param {string} [params.savedPredictionId] - Saved prediction id for owned-report comps access
 * @returns {Promise<{ comps: Array, count: number, message?: string }>} Full response with comps array and optional message when none found
 */
export const getCustomComps = async (params) => {
  const {
    city,
    propertyType,
    lat,
    lon,
    address,
    sqft,
    bedrooms,
    bathrooms,
    yearBuilt,
    lotSize,
    houseStyle,
    neighbourhood,
    limit = 10,
    predictedPrice,
    hasGarage,
    overallCondition,
    halfBaths,
    hasBasement,
    basementFinished,
    basementUnfinished,
    stripeSessionId,
    savedPredictionId,
  } = params

  if (!city || !propertyType || !['house', 'condo'].includes(propertyType)) {
    throw new Error('City and property type (house/condo) are required')
  }
  if ((lat == null || lon == null) && !address) {
    throw new Error('lat/lon or address are required for custom comps')
  }
  if (sqft == null || bedrooms == null || bathrooms == null) {
    throw new Error('sqft, bedrooms, and bathrooms are required for custom comps')
  }

  const body = {
    city,
    property_type: propertyType,
    sqft: Number(sqft),
    bedrooms: Number(bedrooms),
    bathrooms: Number(bathrooms),
    limit: Math.min(20, Math.max(1, Number(limit) || 10))
  }
  if (lat != null) body.lat = Number(lat)
  if (lon != null) body.lon = Number(lon)
  if (address) body.address = address
  if (yearBuilt != null) body.year_built = Number(yearBuilt)
  if (lotSize != null) body.lot_size = Number(lotSize)
  if (houseStyle) body.house_style = houseStyle
  if (neighbourhood) body.neighbourhood = neighbourhood
  if (predictedPrice != null) body.predicted_price = Number(predictedPrice)
  if (hasGarage != null) body.has_garage = Number(hasGarage)
  if (overallCondition != null) body.overall_condition = Number(overallCondition)
  if (halfBaths != null) body.half_baths = Number(halfBaths)
  if (hasBasement != null) body.has_basement = Number(hasBasement)
  if (basementFinished != null) body.basement_finished = Number(basementFinished)
  if (basementUnfinished != null) body.basement_unfinished = Number(basementUnfinished)
  if (stripeSessionId) {
    const sessionId = String(stripeSessionId)
    // Send both forms for backward/forward compatibility while endpoints converge.
    body.stripe_session_id = sessionId
    body.stripeSessionId = sessionId
  }
  if (savedPredictionId) {
    const savedId = String(savedPredictionId)
    body.saved_prediction_id = savedId
    body.savedPredictionId = savedId
  }

  const headers = await getHeadersWithAuth()
  const response = await fetch(`${API_BASE_URL}/api/v1/comps/custom`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const err = new Error(errorData.detail || 'Failed to fetch custom comps')
    err.status = response.status
    throw err
  }

  const data = await response.json()
  return { comps: data.comps || [], count: data.count ?? 0, message: data.message }
}

/**
 * Free preview comps — returns count + basic stats (no prices/adjustments). IP rate limited 1/day.
 */
export const getCustomCompsPreview = async (params) => {
  const {
    city, propertyType, lat, lon, address, sqft, bedrooms, bathrooms,
    yearBuilt, lotSize, houseStyle, neighbourhood, overallCondition, limit = 5,
  } = params

  if (!city || !propertyType) throw new Error('City and property type are required')

  const body = { city, property_type: propertyType, limit }
  if (lat != null) body.lat = Number(lat)
  if (lon != null) body.lon = Number(lon)
  if (address) body.address = address
  if (sqft) body.sqft = Number(sqft)
  if (bedrooms) body.bedrooms = Number(bedrooms)
  if (bathrooms != null) body.bathrooms = Number(bathrooms)
  if (yearBuilt) body.year_built = Number(yearBuilt)
  if (lotSize) body.lot_size = Number(lotSize)
  if (houseStyle) body.house_style = houseStyle
  if (neighbourhood) body.neighbourhood = neighbourhood
  if (overallCondition != null) body.overall_condition = Number(overallCondition)

  const response = await fetch(`${API_BASE_URL}/api/v1/comps/custom/preview`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  })

  if (response.status === 429) {
    return { comps: [], count: 0, rateLimited: true }
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const err = new Error(errorData.detail || 'Failed to fetch comps preview')
    err.status = response.status
    throw err
  }

  const data = await response.json()
  return { comps: data.comps || [], count: data.count ?? 0 }
}

/**
 * Compute SHAP-based adjustments for custom comps (on-demand, compute-intensive).
 * Call this when user switches to "Data-Driven" adjustment method.
 * 
 * @param {Object} params - Same params as getCustomComps
 * @param {Array} params.comps - The comps array to add SHAP adjustments to
 * @returns {Promise<{ comps: Array }>} Comps with SHAP adjustment fields added
 */
export const getCustomCompsShap = async (params) => {
  const {
    city,
    propertyType,
    lat,
    lon,
    sqft,
    bedrooms,
    bathrooms,
    yearBuilt,
    lotSize,
    houseStyle,
    neighbourhood,
    predictedPrice,
    hasGarage,
    overallCondition,
    halfBaths,
    hasBasement,
    basementFinished,
    basementUnfinished,
    comps  // The existing comps to add SHAP to
  } = params

  if (!city || !propertyType || !comps || comps.length === 0) {
    throw new Error('City, property type, and comps array are required')
  }

  const body = {
    city,
    property_type: propertyType,
    lat: Number(lat),
    lon: Number(lon),
    sqft: Number(sqft),
    bedrooms: Number(bedrooms),
    bathrooms: Number(bathrooms),
    comps
  }
  if (yearBuilt != null) body.year_built = Number(yearBuilt)
  if (lotSize != null) body.lot_size = Number(lotSize)
  if (houseStyle) body.house_style = houseStyle
  if (neighbourhood) body.neighbourhood = neighbourhood
  if (predictedPrice != null) body.predicted_price = Number(predictedPrice)
  if (hasGarage != null) body.has_garage = Number(hasGarage)
  if (overallCondition != null) body.overall_condition = Number(overallCondition)
  if (halfBaths != null) body.half_baths = Number(halfBaths)
  if (hasBasement != null) body.has_basement = Number(hasBasement)
  if (basementFinished != null) body.basement_finished = Number(basementFinished)
  if (basementUnfinished != null) body.basement_unfinished = Number(basementUnfinished)

  const headers = await getHeadersWithAuth()
  const response = await fetch(`${API_BASE_URL}/api/v1/comps/custom/shap`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const err = new Error(errorData.detail || 'Failed to compute data-driven adjustments')
    err.status = response.status
    throw err
  }

  const data = await response.json()
  return { comps: data.comps || [], shap_available: data.shap_available, message: data.message }
}

/**
 * Get market context buckets showing price ranges by size for a given area.
 * 
 * Returns aggregated price ranges (min-max) grouped by size buckets for a 
 * neighbourhood and property type/style. This helps users understand how 
 * their property compares to similar sales.
 * 
 * @param {string} city - City name
 * @param {string} propertyType - Property type: 'house' or 'condo'
 * @param {string} [neighbourhood] - Neighbourhood to filter by (optional)
 * @param {string} [houseStyle] - House style to filter by (optional, houses only)
 * @returns {Promise<Object>} Market context with buckets array, total_sales, and filters_applied
 */
export const getMarketContext = async (city, propertyType, neighbourhood = null, houseStyle = null) => {
  if (!city) {
    throw new Error('City is required')
  }
  if (!propertyType || !['house', 'condo'].includes(propertyType)) {
    throw new Error('Property type must be "house" or "condo"')
  }

  let url = `${API_BASE_URL}/api/v1/market-context/${encodeURIComponent(city)}?property_type=${encodeURIComponent(propertyType)}`
  if (neighbourhood) {
    url += `&neighbourhood=${encodeURIComponent(neighbourhood)}`
  }
  if (houseStyle && propertyType === 'house') {
    url += `&house_style=${encodeURIComponent(houseStyle)}`
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders()
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const error = new Error(errorData.detail || `Failed to fetch market context for ${city}`)
      error.status = response.status
      throw error
    }

    return await response.json()
  } catch (error) {
    if (error.status) {
      throw error
    } else {
      const networkError = new Error(
        error.message || `Failed to fetch market context. Please check your connection and try again.`
      )
      networkError.originalError = error
      networkError.isNetworkError = true
      throw networkError
    }
  }
}

/**
 * Get recent sold listings for a city (basic comps for free tier)
 * 
 * @param {string} city - City name
 * @param {string} propertyType - Property type: 'house' or 'condo'
 * @param {string} [neighborhood] - Optional neighborhood filter
 * @param {number} [limit=50] - Maximum number of listings to return
 * @returns {Promise<Array>} Array of sold listing objects
 */
export const getSoldListings = async (city, propertyType, neighborhood = null, limit = 50) => {
  if (!city) {
    throw new Error('City is required')
  }
  if (!propertyType || !['house', 'condo'].includes(propertyType)) {
    throw new Error('Property type must be "house" or "condo"')
  }

  let url = `${API_BASE_URL}/api/v1/sold-listings/${encodeURIComponent(city)}?property_type=${encodeURIComponent(propertyType)}&limit=${limit}`
  if (neighborhood) {
    url += `&neighborhood=${encodeURIComponent(neighborhood)}`
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders()
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const error = new Error(errorData.detail || `Failed to fetch sold listings for ${city}`)
      error.status = response.status
      throw error
    }

    const data = await response.json()
    
    // Return the listings array
    return data.listings || []
  } catch (error) {
    if (error.status) {
      throw error
    } else {
      const networkError = new Error(
        error.message || `Failed to fetch sold listings. Please check your connection and try again.`
      )
      networkError.originalError = error
      networkError.isNetworkError = true
      throw networkError
    }
  }
}

/**
 * Fetch a single property prediction by its property_id.
 * Used by PropertyDetails when loading from a shared URL (/property/:id).
 *
 * @param {string} propertyId - The unique property identifier
 * @param {string} [propertyType] - Optional: 'house' or 'condo' (backend tries both if omitted)
 * @param {string|null} [unlockSessionId] - Guest Quick Unlock Stripe session (full prediction intervals)
 * @returns {Promise<Object>} Prediction object
 */
export const getPropertyById = async (propertyId, propertyType = null, unlockSessionId = null) => {
  if (!propertyId) throw new Error('propertyId is required')

  const headers = await getHeadersWithAuth()
  let url = `${API_BASE_URL}/api/v1/property/${encodeURIComponent(propertyId)}`
  const qs = new URLSearchParams()
  if (propertyType) qs.set('property_type', propertyType)
  if (unlockSessionId) qs.set('unlock_session_id', unlockSessionId)
  const q = qs.toString()
  if (q) url += `?${q}`

  try {
    const response = await fetch(url, { method: 'GET', headers })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const error = new Error(errorData.detail || `Property not found`)
      error.status = response.status
      throw error
    }
    return await response.json()
  } catch (error) {
    if (error.status) throw error
    const networkError = new Error(error.message || 'Failed to fetch property. Please check your connection.')
    networkError.originalError = error
    networkError.isNetworkError = true
    throw networkError
  }
}

/**
 * Remove a saved prediction from the authenticated account.
 *
 * @param {string} predictionId - Saved prediction id to remove
 * @returns {Promise<{success:boolean}>}
 */
export const deleteSavedPrediction = async (predictionId) => {
  if (!predictionId) throw new Error('predictionId is required')
  const headers = await getHeadersWithAuth()
  const response = await fetch(
    `${API_BASE_URL}/api/v1/me/saved-predictions/${encodeURIComponent(predictionId)}`,
    { method: 'DELETE', headers }
  )
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    const e = new Error(err.detail || 'Failed to remove saved prediction')
    e.status = response.status
    throw e
  }
  return await response.json()
}

/**
 * Remove one unlocked report entitlement from the authenticated account.
 *
 * @param {string} propertyId - Property identifier to remove
 * @returns {Promise<{success:boolean, unlocked_properties?:string[]}>}
 */
export const deleteUnlockedReport = async (propertyId) => {
  if (!propertyId) throw new Error('propertyId is required')
  const headers = await getHeadersWithAuth()
  const response = await fetch(
    `${API_BASE_URL}/api/v1/me/unlocked-reports/${encodeURIComponent(propertyId)}`,
    { method: 'DELETE', headers }
  )
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    const e = new Error(err.detail || 'Failed to remove unlocked report')
    e.status = response.status
    throw e
  }
  return await response.json()
}

/**
 * Fetch the neighbourhood boundary FeatureCollection for a city.
 * Results are cached in-module by city name so repeated calls within
 * a session don't re-fetch (boundaries don't change).
 */
const _neighbourhoodBoundaryCache = {}
export const getNeighbourhoodBoundaries = async (city) => {
  if (_neighbourhoodBoundaryCache[city]) return _neighbourhoodBoundaryCache[city]
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/neighbourhoods/${encodeURIComponent(city)}/boundaries`,
      { method: 'GET', headers: getHeaders() }
    )
    if (!response.ok) return null
    const data = await response.json()
    _neighbourhoodBoundaryCache[city] = data
    return data
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Lead gen — email-gated PDF and agent connect (no auth required)
// ---------------------------------------------------------------------------

/**
 * Request an anonymous email-gated PDF export.
 * Rate limited: 3/day per IP (backend slowapi) + 3/day per email (backend DB).
 *
 * @param {Object} params
 * @param {string} params.email
 * @param {boolean} params.marketing_opt_in
 * @param {Object} params.prediction
 * @param {Object} params.features
 * @param {Array}  params.comps
 * @param {string} params.address
 * @param {string} params.city
 * @param {string} params.property_type
 * @returns {Promise<{job_id: string, position: number}>}
 */
export async function requestEmailPdf({ email, marketing_opt_in = false, prediction, features = {}, comps = [], address, city, property_type }) {
  const res = await fetch(`${API_BASE_URL}/api/v1/export/pdf-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, marketing_opt_in, prediction, features, comps, address, city, property_type }),
  })
  if (res.status === 429) {
    const data = await res.json().catch(() => ({}))
    const err = new Error(data.detail || 'Daily PDF download limit reached. Try again tomorrow or upgrade to Pro.')
    err.isRateLimited = true
    throw err
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.detail || 'Failed to generate PDF report. Please try again.')
  }
  return res.json()
}

/**
 * Submit a Connect with Agent lead form.
 * Rate limited: 5/day per IP.
 *
 * @param {Object} params
 * @param {string} params.name
 * @param {string} params.email
 * @param {string} [params.phone]
 * @param {string} params.city
 * @param {string} [params.property_type]
 * @param {string} [params.address_valued]
 * @param {number} [params.predicted_price]
 * @param {string} [params.intent] - 'buying' | 'selling' | 'refinancing' | 'just_curious'
 * @returns {Promise<{success: boolean, id: string}>}
 */
export async function submitAgentLead({ name, email, phone, city, property_type, address_valued, predicted_price, intent }) {
  const res = await fetch(`${API_BASE_URL}/api/v1/leads/buyer-seller-connect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, phone, city, property_type, address_valued, predicted_price, intent }),
  })
  if (res.status === 429) {
    const err = new Error('Too many submissions. Please try again tomorrow.')
    err.isRateLimited = true
    throw err
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.detail || 'Failed to submit. Please try again.')
  }
  return res.json()
}

/**
 * Submit a Realtor+ program lead form.
 * Rate limited: 10/day per IP.
 *
 * @param {Object} params
 * @param {string} params.name
 * @param {string} params.email
 * @param {string} [params.phone]
 * @param {string} params.city_or_service_area
 * @param {string} [params.brokerage]
 * @param {string} [params.license_id]
 * @param {number} [params.years_experience]
 * @param {string} [params.source]
 * @param {string} [params.utm_source]
 * @param {string} [params.utm_medium]
 * @param {string} [params.utm_campaign]
 * @param {string} [params.utm_term]
 * @param {string} [params.utm_content]
 * @param {string} [params.notes]
 * @returns {Promise<{success: boolean, id: string}>}
 */
export async function submitRealtorProgramLead(params) {
  const res = await fetch(`${API_BASE_URL}/api/v1/leads/agent-program`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  })
  if (res.status === 429) {
    const err = new Error('Too many submissions. Please try again tomorrow.')
    err.isRateLimited = true
    throw err
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.detail || 'Failed to submit. Please try again.')
  }
  return res.json()
}

// ---------------------------------------------------------------------------
// Listing votes — community polls on browse cards and reports
// ---------------------------------------------------------------------------

/**
 * Submit or update a vote on a listing.
 * @returns {Promise<{property_id, yes_count, no_count, lower_price_count, total_votes, user_vote}>}
 */
export async function submitListingVote({ property_id, voter_id, vote, voter_type = 'anonymous', city, property_type }) {
  const res = await fetch(`${API_BASE_URL}/api/v1/votes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ property_id, voter_id, vote, voter_type, city, property_type }),
  })
  if (res.status === 429) {
    const err = new Error('Too many votes. Please slow down.')
    err.isRateLimited = true
    throw err
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.detail || 'Failed to submit vote')
  }
  return res.json()
}

/**
 * Get vote aggregates for a single listing.
 * @returns {Promise<{property_id, yes_count, no_count, lower_price_count, total_votes, user_vote}>}
 */
export async function getListingVote(property_id, voter_id) {
  const params = new URLSearchParams()
  if (voter_id) params.set('voter_id', voter_id)
  const res = await fetch(`${API_BASE_URL}/api/v1/votes/${encodeURIComponent(property_id)}?${params}`)
  if (!res.ok) return { property_id, yes_count: 0, no_count: 0, lower_price_count: 0, total_votes: 0, user_vote: null }
  return res.json()
}

/**
 * Batch fetch vote aggregates for browse card display.
 * @param {string[]} propertyIds
 * @param {string} [voterId]
 * @returns {Promise<Array<{property_id, yes_count, no_count, lower_price_count, total_votes, user_vote}>>}
 */
export async function getListingVotesBatch(propertyIds, voterId) {
  if (!propertyIds.length) return []
  const params = new URLSearchParams({ ids: propertyIds.join(',') })
  if (voterId) params.set('voter_id', voterId)
  const res = await fetch(`${API_BASE_URL}/api/v1/votes?${params}`)
  if (!res.ok) return propertyIds.map(id => ({ property_id: id, yes_count: 0, no_count: 0, lower_price_count: 0, total_votes: 0, user_vote: null }))
  return res.json()
}

/**
 * Get top voted property_ids in a city (for "Most discussed" sort).
 * @returns {Promise<Array<{property_id, total_votes}>>}
 */
export async function getTopVotedListings(city, { limit = 50, property_type } = {}) {
  const params = new URLSearchParams()
  if (limit) params.set('limit', String(limit))
  if (property_type) params.set('property_type', property_type)
  const res = await fetch(`${API_BASE_URL}/api/v1/votes/top/${encodeURIComponent(city)}?${params}`)
  if (!res.ok) return []
  return res.json()
}

// Export API configuration for debugging/testing
export const apiConfig = {
  baseUrl: API_BASE_URL,
}
